import type { DroneTelemetry } from '@shared/types';

/**
 * 遥测写 store 的节流器。
 *
 * 硬实时视图直接订阅总线，全速渲染；写进 store 的帧只用于普通 React 渲染，
 * 因此限制在 5Hz 左右。这里把「首帧立即写 + 尾帧补写最新帧」的逻辑独立出来，
 * 一是避免重复实现，二是它出错的后果很隐蔽：如果尾帧补写的是旧值，
 * store 里的 `receivedAt` 会停在过去，界面就会常驻一个「链路失联」的严重告警，
 * 而同一屏里走总线的数字仍然是新鲜的。必须有单测钉住这个行为。
 */
export interface TelemetryThrottleOptions {
  /** 两次 store 写入的最小间隔。 */
  intervalMs: number;
  /** 由调用方注入时钟与定时器，方便单测用假定时器驱动。 */
  now: () => number;
  setTimer: (callback: () => void, delayMs: number) => number;
  clearTimer: (handle: number) => void;
  /** 真正写入 store 的回调，参数一定是最新到达的帧。 */
  write: (telemetry: DroneTelemetry) => void;
}

export interface TelemetryThrottle {
  /** 推入一帧；返回是否发生了同步写入（供调试/断言使用）。 */
  push: (telemetry: DroneTelemetry) => boolean;
  /** 链路释放时清掉挂起的定时器与最近帧。 */
  reset: () => void;
}

export function createTelemetryThrottle(options: TelemetryThrottleOptions): TelemetryThrottle {
  const { intervalMs, now, setTimer, clearTimer, write } = options;
  let lastWriteAt = 0;
  let pendingTimer: number | null = null;
  let latest: DroneTelemetry | null = null;

  const cancelPending = () => {
    if (pendingTimer === null) return;
    clearTimer(pendingTimer);
    pendingTimer = null;
  };

  const flush = (telemetry: DroneTelemetry) => {
    cancelPending();
    lastWriteAt = now();
    write(telemetry);
  };

  return {
    push(telemetry) {
      latest = telemetry;
      const elapsed = now() - lastWriteAt;
      if (elapsed >= intervalMs) {
        flush(telemetry);
        return true;
      }
      if (pendingTimer !== null) return false;
      pendingTimer = setTimer(() => {
        pendingTimer = null;
        // 关键：补写的是「最新帧」。写 store 里已有的值只会让 store 停在旧帧上。
        if (latest) flush(latest);
      }, intervalMs - elapsed);
      return false;
    },
    reset() {
      cancelPending();
      latest = null;
      lastWriteAt = 0;
    },
  };
}
